import React, { useState, useRef } from 'react'

import usFlagSmall from '../../../node_modules/uswds/dist/img/us_flag_small.png'
import iconDotGov from '../../../node_modules/uswds/dist/img/icon-dot-gov.svg'
import iconHttps from '../../../node_modules/uswds/dist/img/icon-https.svg'

import { useTranslation, Trans } from 'react-i18next'

import events, { Event } from 'pubsub/events'

import { Categories } from 'constants/analytics'
import { getChangeLanguageLinkLang } from 'utils/getChangeLanguageLinkLang'

const IconLock = () => (
  <span>
    <svg xmlns='http://www.w3.org/2000/svg' width='52' height='64' viewBox='0 0 52 64' className='usa-banner__lock-image' role='img' aria-labelledby='banner-lock-title banner-lock-description' focusable='false'>
      <title id='banner-lock-title'>lock</title>
      <desc id='banner-lock-description'>A locked padlock</desc>
      <path fill='#000000' fillRule='evenodd' d='M26 0c10.493 0 19 8.507 19 19v9h3a4 4 0 0 1 4 4v28a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V32a4 4 0 0 1 4-4h3v-9C7 8.507 15.507 0 26 0zm0 8c-5.979 0-10.843 4.77-10.996 10.712L15 19v9h22v-9c0-6.075-4.925-11-11-11z' />
    </svg>
  </span>
)

export interface GovBannerProps {
  languageToggleLink: string;
  isLoggedIn: boolean;
}

export const GovBanner: React.FC<GovBannerProps> = ({ languageToggleLink, isLoggedIn }) => {
  const buttonRef:React.RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null)
  const [isOpen, setOpenState] = useState(false)
  const { t, i18n } = useTranslation('govBanner')

  const handleLanuageToggleCLick = () => {
    const clickEvent: Event = {
      authenticated: isLoggedIn,
      text: t('changeLanguage'),
      targetLocation: languageToggleLink,
      category: Categories.LanguageSelection,
      activity: 'Link Click',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: true,
      sendToCoreAPI: true,
      isMessageCenter: false
    }

    events.publish(clickEvent)
  }

  const handleGovBannerToggleClick = () => {
    if (buttonRef.current) {
      buttonRef.current.focus()
    }
    const clickEventOptions: Event = {
      authenticated: isLoggedIn,
      text: t('main.link'),
      category: Categories.GovBanner,
      activity: 'Link Click',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: true,
      sendToCoreAPI: true,
      isMessageCenter: false
    }

    events.publish(clickEventOptions)

    setOpenState(!isOpen)
  }

  return (
    <section className='usa-banner' aria-label='Official government website'>
      <div className='usa-accordion'>
        <header className='usa-banner__header no-min-height'>
          <div className='usa-banner__inner'>
            <div className='grid-col-auto'>
              <img className='usa-banner__header-flag' src={usFlagSmall} alt='U.S. flag' />
            </div>

            <div className='grid-col-fill tablet:grid-col-auto'>
              <p className='usa-banner__header-text'>{t('main.message')}</p>
              <p className='usa-banner__header-action' aria-hidden='true'>{t('main.link')}</p>
            </div>

            <button
              ref={buttonRef}
              className='usa-accordion__button usa-banner__button'
              aria-expanded={isOpen} aria-controls='gov-banner'
              data-testid='how-you-know-btn'
              type='button'
              onClick={handleGovBannerToggleClick}
            >
              <span className='usa-banner__button-text ds-u-color--primary-alt'>{t('main.link')}</span>
            </button>

            <div className='grid-col-fill ch-u-remove--md' />

            <div className='grid-col-auto language-toggle ch-u-remove--md'>
              <a className='ch-c-change-language-link' data-testid='lng-tgl-btn' href={languageToggleLink} lang={getChangeLanguageLinkLang(i18n.language)} onClick={handleLanuageToggleCLick}>{t('changeLanguage')}</a>
            </div>
          </div>
        </header>
        <div className='usa-banner__content usa-accordion__content' id='gov-banner' hidden={!isOpen}>
          <div className='grid-row grid-gap-lg'>
            <div className='usa-banner__guidance tablet:grid-col-6'>
              <img className='usa-banner__icon usa-media-block__img' src={iconDotGov} role='img' alt='' aria-hidden='true' />
              <div className='usa-media-block__body'>
                <p>
                  <strong data-testid='dotgov-label'>
                    {t('expanded.dotGov.label')}
                  </strong>
                  <br />
                  <Trans i18nKey='govBanner:expanded.dotGov.description'>
                    A <strong>.gov</strong> website belongs to an official government organization in the United States.
                  </Trans>
                </p>
              </div>
            </div>
            <div className='usa-banner__guidance tablet:grid-col-6'>
              <img className='usa-banner__icon usa-media-block__img' src={iconHttps} role='img' alt='' aria-hidden='true' />
              <div className='usa-media-block__body'>
                <p>
                  <strong>
                    {t('expanded.https.label')}
                  </strong>
                  <br />
                  <Trans i18nKey='govBanner:expanded.https.description'>
                    A <strong>lock</strong> ( <IconLock /> ) or <strong>https://</strong> means you’ve safely connected to the .gov website. Share sensitive information only on official, secure websites.
                  </Trans>

                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
